import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Control, Controller } from "react-hook-form";
import { Box, FormControl, FormLabel, Typography, SxProps, Alert, Button, FormHelperText } from "@mui/material";

import { Media } from "../../../../../types/types/common.types";

import { MediaUploadError } from "../../../../../api/v1/types/media.types";
import { useMultipleMediaUpload } from "../../../../../api/v1/mutations/useMediaUpload";
import { formatMediaResponse } from "../../../../../api/v1/formatters/media.formatters";

import { getVideoDuration } from "../../../utils/getVideoDuration";
import { getVideoThumbnail } from "../../../utils/getVideoThumbnail";
import { CreatePostDragAndDropIcon } from "../../../assets/icons/create-feed";
import { CreatePostFormData, UploadingMedia } from "../../../api/types/feed.types";
import { commonStyles } from "../../../pages/CreatePostPage/components/CreatePostForm";

import { useGetUserSettings } from "../../../../Plans/api/queries/useGetUserSettings";

import { MediaPreview } from "./MediaPreview";
import { UploadingMediaPreview } from "./UploadingMediaPreview";

const styles = {
  errorAlert: {
    marginTop: "8px",
  } as SxProps,
  uploadingText: {
    color: "var(--Brand-700, #076D0D)",
    textAlign: "center",
    fontFamily: "Montserrat",
    fontSize: "10.5px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "15px",
  } as SxProps,
  previewContainer: {
    display: "flex",
    gap: "8px",
    overflow: "auto",
  } as SxProps,
  previewItem: {
    position: "relative",
    width: "160px",
    aspectRatio: "1",
    borderRadius: "8px",
    overflow: "hidden",
  } as SxProps,
  previewImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  } as SxProps,
  previewVideo: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0, 0, 0, 0.1)",
  } as SxProps,
  removeButton: {
    position: "absolute",
    top: 4,
    right: 4,
    padding: "4px",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.9)",
    },
  } as SxProps,
  uploadProgress: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    padding: "4px",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    textAlign: "center",
    fontSize: "12px",
  } as SxProps,
  progressOverlay: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  } as SxProps,
  progressText: {
    position: "absolute",
    color: "#FFF",
    textAlign: "center",
    fontFamily: "Montserrat",
    fontSize: "10.5px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "15px",
  } as SxProps,
  dropZone: (dragActive: boolean) => ({
    position: "relative",
    borderRadius: "12px",
    border: `1px solid ${dragActive ? "var(--Brand-700, #076D0D)" : "var(--Gray-200, #E4E7EC)"}`,
    background: "var(--Base-White, #FFF)",
    textAlign: "center",
    cursor: "pointer",
    padding: "24px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    gap: "12px",
  }),
} as const;

interface MediaSectionProps {
  control: Control<CreatePostFormData>;
  name: "media";
  error?: string;
}

export const MediaField = ({ control, name, error }: MediaSectionProps) => {
  const { t } = useTranslation("feed", { keyPrefix: "postForm" });
  const [dragActive, setDragActive] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState<UploadingMedia[]>([]);
  const [uploadError, setUploadError] = useState<MediaUploadError | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const { mutate: uploadMedia, isPending } = useMultipleMediaUpload();
  const { data: userSettings } = useGetUserSettings();

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent, onChange: (value: Media[]) => void, value: Media[]) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setUploadError(null);

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files, onChange, value);
  };

  const handleFiles = async (files: File[], onChange: (value: Media[]) => void, value: Media[]) => {
    setUploadError(null);
    setIsUploading(true);
    const mediaFiles = files.filter((file) => file.type.startsWith("image/") || file.type.startsWith("video/"));

    if (mediaFiles.length === 0) {
      setUploadError({
        code: "INVALID_FILE_TYPE",
        message: "Please upload only image or video files",
      });
      setIsUploading(false);
      return;
    }

    const maxVideoDurationSeconds = userSettings?.maxVideoDurationSeconds;
    const rejectedVideos: { fileName: string; duration: number }[] = [];
    const validMediaFiles: File[] = [];

    // Check video durations before processing
    for (const file of mediaFiles) {
      if (file.type.startsWith("video/")) {
        // Only check duration if limit exists
        if (maxVideoDurationSeconds !== undefined) {
          try {
            const duration = await getVideoDuration(file);
            if (duration > maxVideoDurationSeconds) {
              rejectedVideos.push({ fileName: file.name, duration });
              continue;
            }
          } catch (error) {
            console.error("Failed to get video duration:", error);
            // If we can't get duration, allow the video to proceed
          }
        }
        validMediaFiles.push(file);
      } else {
        validMediaFiles.push(file);
      }
    }

    // Show error for rejected videos
    if (rejectedVideos.length > 0 && maxVideoDurationSeconds !== undefined) {
      const errorMessages = rejectedVideos.map(({ fileName, duration }) =>
        t("videoExceedsDurationLimit", {
          fileName,
          actual: duration,
          limit: maxVideoDurationSeconds,
        })
      );
      setUploadError({
        code: "VIDEO_DURATION_EXCEEDED",
        message: errorMessages.join("\n"),
      });
    }

    // If no valid files after filtering, stop here
    if (validMediaFiles.length === 0) {
      setIsUploading(false);
      return;
    }

    // Prepare files and their thumbnails
    const uploadQueue: { file: File; type: "IMAGE" | "VIDEO"; thumbnailFile?: File }[] = [];

    for (const file of validMediaFiles) {
      if (file.type.startsWith("video/")) {
        try {
          const thumbnailFile = await getVideoThumbnail(file);
          uploadQueue.push({
            file,
            type: "VIDEO",
            thumbnailFile,
          });
        } catch (error) {
          console.error("Failed to create video thumbnail:", error);
          uploadQueue.push({
            file,
            type: "VIDEO",
          });
        }
      } else {
        uploadQueue.push({
          file,
          type: "IMAGE",
        });
      }
    }

    const newUploadingMedia = uploadQueue.map(({ file, type }) => ({
      type,
      file,
      thumbnail: type === "VIDEO" ? URL.createObjectURL(file) : undefined,
      progress: 0,
      fileName: file.name,
    }));

    setUploadingMedia((prev) => [...prev, ...newUploadingMedia]);

    // Upload all files including thumbnails
    const filesToUpload = uploadQueue.flatMap(({ file, thumbnailFile }) =>
      thumbnailFile ? [file, thumbnailFile] : [file]
    );

    uploadMedia(
      {
        files: filesToUpload,
        onProgress: (progress) => {
          setUploadingMedia((prev) =>
            prev.map((item) => {
              const fileProgress = progress.find((p) => p.fileName === item.fileName);
              return fileProgress ? { ...item, progress: fileProgress.progress } : item;
            })
          );
        },
      },
      {
        onSuccess: (responses) => {
          const formattedResponses = responses.map(formatMediaResponse);

          // Match responses with original files
          let currentIndex = 0;
          const newMedia = uploadQueue.map(({ type }) => {
            if (type === "VIDEO") {
              const videoResponse = formattedResponses[currentIndex];
              const thumbnailResponse = formattedResponses[currentIndex + 1];
              currentIndex += 2;
              return {
                type: "VIDEO" as const,
                url: videoResponse.fileName,
                thumbnail: thumbnailResponse.fileName,
              };
            } else {
              const imageResponse = formattedResponses[currentIndex];
              currentIndex += 1;
              return {
                type: "IMAGE" as const,
                url: imageResponse.fileName,
              };
            }
          });

          onChange([...value, ...newMedia]);
          setUploadingMedia([]);
        },
        onError: (error: MediaUploadError) => {
          setUploadError(error);
          setUploadingMedia([]);
        },
        onSettled: () => {
          setIsUploading(false);
        },
      }
    );
  };

  const handleRemoveUploadingMedia = (fileName: string) => {
    setUploadingMedia((prev) => prev.filter((item) => item.fileName !== fileName));
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange } }) => {
        const mediaValue: Media[] = value || [];
        return (
          <FormControl fullWidth component="fieldset" sx={commonStyles.formControl} error={!!error}>
            <FormLabel sx={commonStyles.formLabel}>{t("addMedia")}</FormLabel>
            <input
              type="file"
              id="file-upload"
              multiple
              accept="image/*,video/*"
              style={{ display: "none" }}
              onChange={(e) => handleFiles(Array.from(e.target.files || []), onChange, mediaValue)}
              disabled={isPending}
            />

            {mediaValue.length === 0 && !isUploading && (
              <Box
                sx={{
                  ...styles.dropZone(dragActive),
                  ...(error && { border: "1px solid #d32f2f" }),
                }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={(e) => handleDrop(e, onChange, mediaValue)}
                onClick={() => !isPending && document.getElementById("file-upload")?.click()}
              >
                <CreatePostDragAndDropIcon />
                <Typography
                  sx={{
                    color: "var(--Gray-600, #475467)",
                    fontFamily: "Montserrat",
                    fontSize: "14px",
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "20px",
                    maxWidth: "252px",
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      color: "var(--Brand-700, #076D0D)",
                      fontFamily: "Montserrat",
                      fontSize: "14px",
                      fontStyle: "normal",
                      fontWeight: 600,
                      lineHeight: "20px",
                      display: "inline-block",
                      marginRight: "4px",
                    }}
                  >
                    {t("clickToUpload")}
                  </Typography>
                  {t("dragAndDrop")}
                </Typography>
              </Box>
            )}

            {uploadError && (
              <Alert severity="error" sx={styles.errorAlert}>
                {uploadError.message}
              </Alert>
            )}

            {error && <FormHelperText error>{error}</FormHelperText>}

            {(mediaValue.length > 0 || uploadingMedia.length > 0 || isUploading) && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <Box sx={styles.previewContainer}>
                  {mediaValue.map((item, index) => (
                    <MediaPreview
                      key={item.url}
                      item={item}
                      onRemove={() => {
                        const newMedia = [...mediaValue];
                        newMedia.splice(index, 1);
                        onChange(newMedia);
                      }}
                    />
                  ))}

                  {uploadingMedia.map((item) => (
                    <UploadingMediaPreview
                      key={item.fileName}
                      item={item}
                      onRemove={() => handleRemoveUploadingMedia(item.fileName)}
                    />
                  ))}
                </Box>

                <Button variant="outlined-md" fullWidth onClick={() => document.getElementById("file-upload")?.click()}>
                  {t("addMedia")}
                </Button>
              </Box>
            )}
          </FormControl>
        );
      }}
    />
  );
};
